'use client';
import { motion, Variants } from 'motion/react';
import React, { useLayoutEffect, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import cssBreakpoints from '@/cssBreakpoints';
import { useRevealTextAnimation } from '@/hooks/animations/useRevealTextAnimation';
import type { Hero, Media } from '@/payload-types';
import { cn } from '@/utils/cn';

import { HeroContent } from '../HeroContent';
import styles from './ImageStackHero.module.scss';

type Props = Hero & {
  locale?: string;
};

// Variants for photo stack
const photoStackContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const photoStackVariants = (
  index: number,
  amount: number,
  isMobile: boolean
): Variants => ({
  hidden: {
    opacity: 0,
    y: isMobile ? '100%' : '0%',
    x: isMobile ? '0%' : '100%',
    rotate:
      index === amount - 1
        ? 0
        : index % 2 === 0
          ? index % 4 === 0
            ? 12
            : 8
          : index % 4 === 0
            ? -12
            : -8,
  },
  visible: {
    opacity: 1,
    y: '0%',
    x: '0%',
    rotate:
      index === amount - 1
        ? 0
        : index % 2 === 0
          ? index % 4 === 0
            ? 6
            : 2
          : index % 4 === 0
            ? -6
            : -2,

    transition: {
      bounce: 0,
      ease: [0.625, 0.05, 0, 1],
      duration: 0.5,
    },
  },
});

export const ImageStackHero: React.FC<Props> = (props) => {
  const { alignment, backgroundColor, imageStackText, medias } = props;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [hasMounted, setHasMounted] = useState(false);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setIsMobile(width <= cssBreakpoints.small);
    setHasMounted(true);
  }, []);

  const { Component: ImageStackTextComponent } = useRevealTextAnimation({
    text: imageStackText || '',
    type: 'lines',
    delay: 0.5,
    startDelay: 0,
  });

  return (
    <div
      className={cn(
        styles.wrapper,
        backgroundColor ? `${styles[`wrapper--${backgroundColor}`]}` : ''
      )}
      data-hero
      data-background-color={backgroundColor || 'white'}
    >
      <div className={cn('container', styles.container)}>
        <HeroContent {...props} />
        <motion.div
          className={cn(styles.media, alignment && styles[alignment])}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={photoStackContainerVariants}
        >
          {imageStackText && (
            <div className={styles.imageStackText}>
              <ImageStackTextComponent />
            </div>
          )}

          {hasMounted &&
            medias?.map((media, index) => {
              const MediaComponent =
                media?.imageStackMediaType === 'image' &&
                media?.imageStackImage ? (
                  <ImageMedia
                    aspectRatio={'portrait'}
                    priority
                    responsiveWidths={{
                      mobile: 343,
                      desktop: 360,
                      largeDesktop: 360,
                    }}
                    image={media?.imageStackImage}
                  />
                ) : media?.imageStackMediaType === 'video' &&
                  media?.imageStackVideoEmbed ? (
                  <VideoMedia
                    aspectRatio={'portrait'}
                    priority
                    videoPoster={media?.imageStackVideoPoster as Media}
                    videoEmbedUrl={media?.imageStackVideoEmbed as string}
                    imageResponsiveWidths={{
                      mobile: 343,
                      desktop: 360,
                      largeDesktop: 360,
                    }}
                    videoIsBackground={
                      media?.imageStackVideoIsBackground as boolean
                    }
                    videoLoops={media?.imageStackVideoLoops as boolean}
                  />
                ) : null;

              return (
                <motion.div
                  key={index}
                  className={styles.mediaItem}
                  variants={photoStackVariants(index, medias.length, isMobile)}
                  style={{
                    zIndex: medias.length + index,
                    willChange: 'opacity, transform',
                  }}
                >
                  {MediaComponent}
                </motion.div>
              );
            })}
        </motion.div>
      </div>
    </div>
  );
};
